import React, {
	createContext,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import {useConfirmation} from '@netx/core-components/Confirmation';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	ModalManagerProvider,
	ModalManagerStore,
} from '@netx/core-components/ModalManager';
import {
	type IContextChildren,
	type IContextRef,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';
import {AssetViewEvent} from '../actions';
import {AssetViewsDetailDialog} from '../components/AssetViewsDetail';
import {useAssetViews, type UseAssetViewsParams} from '../hooks';
import {
	AssetViewTitle,
	AssetViewMessage,
	AssetViewActionLabel,
} from '../locales';

export type AssetViewsContextType = Omit<
	ReturnType<typeof useAssetViews>,
	'createView' | 'deleteView' | 'editView'
> & {
	createView: () => Promise<void>;
	deleteView: (viewId: number) => Promise<void | number>;
	editView: (viewId: number) => Promise<void>;
	openView: (viewId: number) => Promise<void>;
};

export const AssetViewsContext = createContext<
	AssetViewsContextType | undefined
>(undefined);

export const useAssetViewsProvider = () => {
	const context = useContext(AssetViewsContext);
	if (context === undefined) {
		throw new Error(
			'`useAssetViewsProvider` must be used within a `AssetViewsProvider`',
		);
	}
	return context;
};

export type AssetViewsProviderRef = IContextRef<AssetViewsContextType>;

export type AssetViewsProviderProps = UseAssetViewsParams & {
	ref?: AssetViewsProviderRef;
	children: IContextChildren<AssetViewsContextType>;
};

const AssetViewsProviderPrimitive = (
	{children, ...props}: AssetViewsProviderProps,
	ref: AssetViewsProviderRef,
) => {
	const [modalManagerStore] = useState(() => new ModalManagerStore());

	const {deleteView: _deleteView, ...assetViews} = useAssetViews(props);

	const confirm = useConfirmation();

	const deleteView = useConstCallback(async (viewId: number) => {
		if (!props.asset) return;
		const view = assetViews.data?.find((v) => v.viewId === viewId);
		if (!view) return;
		return await confirm({
			title: AssetViewTitle.DELETE_CONFIRM,
			message: AssetViewMessage.DELETE_CONFIRM(
				`${view.name}${view.fileTypeLabel ? `.${view.fileTypeLabel}` : ''}`,
				props.asset.name,
			),
			confirmLabel: AssetViewActionLabel.DELETE,
		}).then(() => {
			modalManagerStore.hide('AssetViewsDetailDialog');
			return _deleteView(viewId);
		});
	});

	const createView = useConstCallback(async () => {
		dispatcher.trigger(AssetViewEvent.UPLOAD, props.asset);
	});

	const editView = useConstCallback(async (viewId: number) => {
		const view = assetViews.data?.find((v) => v.viewId === viewId);
		if (!view) return;
		dispatcher.trigger(AssetViewEvent.EDIT, view);
	});

	const openView = useConstCallback(async (viewId: number) => {
		const index = assetViews.data?.findIndex((v) => v.viewId === viewId);
		if (index < 0) return;
		modalManagerStore.show('AssetViewsDetailDialog', AssetViewsDetailDialog, {
			index,
			asset: props.asset,
		});
	});

	const contextValue = useMemo(
		() => ({...assetViews, deleteView, createView, editView, openView}),
		[assetViews, deleteView, createView, editView, openView],
	);

	useEffect(() => {
		return () => {
			modalManagerStore.dispose();
		};
	}, []);

	useImperativeHandle(ref, () => contextValue, [contextValue]);

	return (
		<AssetViewsContext.Provider value={contextValue}>
			<ModalManagerProvider modalManagerStore={modalManagerStore}>
				{renderChildrenOrChildrenFunction(children, contextValue)}
			</ModalManagerProvider>
		</AssetViewsContext.Provider>
	);
};

/**
 * Asset categories context provider
 * @example
 * <AssetViewsProvider ref={assetViewsRef}>
 * 	...
 * </AssetViewsProvider>
 * @example
 * <AssetViewsProvider ref={assetViewsRef}>
 * 	{(assetViews) => (
 * 		...
 * 	)}
 * </AssetViewsProvider>
 */
export const AssetViewsProvider = React.forwardRef<
	AssetViewsContextType,
	AssetViewsProviderProps
>(AssetViewsProviderPrimitive);

export default AssetViewsProvider;
